'use client'

import { useEffect, useState } from "react";
import { z } from "zod";
import { es } from "date-fns/locale";
import { FormProvider, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";

import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/popadvise/reservations/calendar"
import { FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Separator } from "@/components/ui/separator";
import { ReservationFormProps } from "@/types/popadvise";
import { CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { useCurrentBranch } from "@/context/current-branch-provider";
import { SYSTEM_BE } from "@/lib/constants";
import { toast } from "@/hooks/use-toast";
import Loading from "@/components/system/loading";

export function ReservationsDate({ onContinue, userData, setUserData, eventDate }: {
    onContinue: () => void
    userData: ReservationFormProps
    setUserData: (userData: ReservationFormProps) => void
    eventDate?: string
}) {

    const { currentBranch } = useCurrentBranch()
    const [loading, setLoading] = useState(false)
    const [activeDays, setActiveDays] = useState([])

    const formSchema = z.object({
        date: z.date({ required_error: "Ingresa una fecha de busqueda de reservación" }).nullable().refine((val) => val !== null, {
            message: "Ingresa una fecha de busqueda para la reservación"
        }),
        persons: z.enum(["1", "2", "3", "4", "5", "6", "+"], {
            invalid_type_error: "Selecciona la cantidad de personas para la reservación",
            required_error: "Selecciona la cantidad de personas para la reservación",
        }),
        branch: z.string()
    })

    type FormSchemaType = z.infer<typeof formSchema>

    const form = useForm<FormSchemaType>({
        resolver: zodResolver(formSchema),
        defaultValues: {
            date: userData.date ? new Date(userData.date) : null,
            persons: userData.persons ? userData.persons : '2',
            branch: userData.branch
        }
    })

    const handleOnSubmit = async (data: FormSchemaType) => {
        setUserData({
            ...userData,
            date: data.date.toISOString(),
            persons: data.persons,
            branch: data.branch
        })
        onContinue()
    }

    // const isDayDisabled = (date: Date) => {
    //     const today = new Date();
    //     today.setHours(0, 0, 0, 0);
    //     const dayOfWeek = date.getDay() // 0=Domingo, 6=Sábado
    //     // Como en Java 1=Lunes ... 7=Domingo, adaptamos:
    //     const javaDay = dayOfWeek === 0 ? 7 : dayOfWeek

    //     return today > date && !activeDays.includes(javaDay)
    // }

    const isDayDisabled = (date: Date) => {
        const today = new Date();
        today.setHours(0, 0, 0, 0);
    
        const selected = new Date(date);
        selected.setHours(0, 0, 0, 0);
    
        const dayOfWeek = selected.getDay(); // 0=Domingo
        const javaDay = dayOfWeek === 0 ? 7 : dayOfWeek;
    
        return selected < today || !activeDays.includes(javaDay);
    };

    useEffect(() => {
        setLoading(true)
        fetch(`${SYSTEM_BE}/v1/branch-schedules/active-days?bid=${currentBranch.id}`)
            .then(res => {
                if (!res.ok) {
                    throw new Error("Failed to fetch active days")
                }
                return res.json()
            })
            .then(data => {
                setActiveDays(data.activeDays)
            })
            .catch(() => {
                toast({ title: "Error", description: "Ocurrió un error al intentar cargar los días activos", variant: "destructive" })
            })
            .finally(() => setLoading(false))

    }, [currentBranch.id])

    return (
        <div className="p-6 pt-8">
            <CardHeader className="pl-0">
                <CardTitle className="text-left text-xl">{currentBranch.name}</CardTitle>
                <CardDescription className="text-[#6C6C6C] text-left">
                    Escoge una fecha
                </CardDescription>
            </CardHeader>
            <FormProvider {...form}>
                <form onSubmit={form.handleSubmit(handleOnSubmit)}>
                    {
                        loading && (
                            <div className="flex justify-center items-center">
                                <Loading />
                            </div>
                        )
                    }
                    {
                        !loading && (
                            <div>
                                {
                                    !eventDate && (
                                        <FormField
                                            control={form.control}
                                            name="date"
                                            render={({ field }) => (
                                                <FormItem className="mb-5">
                                                    <FormControl >
                                                        <Calendar
                                                            disabled={isDayDisabled}
                                                            className="border rounded-xl p-4 py-6"
                                                            mode="single"
                                                            selected={field.value}
                                                            onSelect={field.onChange}
                                                            locale={es}
                                                        />
                                                    </FormControl>
                                                    <FormMessage />
                                                </FormItem>
                                            )}
                                        />
                                    )
                                }
                                <FormField
                                    control={form.control}
                                    name="persons"
                                    render={({ field }) => (
                                        <FormItem className="col-span-1 md:col-span-2">
                                            <FormLabel className="text-[#6C6C6C] mb-4 text-sm font-normal">Cantidad de personas</FormLabel>
                                            <FormItem>
                                                <div className="relative">
                                                    <Input className="p-4 py-6" value="2" {...field} />
                                                    <div className="absolute right-0 top-1.5 flex items-center gap-1">
                                                        <Button type="button" variant="ghost" className="text-[#6C6C6C] text-2xl font-normal" onClick={() => {
                                                            if (Number(field.value) > 1) {
                                                                field.onChange(String(Number(field.value) - 1))
                                                            } else if (field.value === '+') {
                                                                form.setValue("persons", "6")
                                                            }
                                                        }}>-</Button>
                                                        <Separator className="w-[1px] h-4" />
                                                        <Button type="button" variant="ghost" className="text-[#6C6C6C] text-2xl font-normal" onClick={() => {
                                                            if (Number(field.value) < 6) {
                                                                field.onChange(String(Number(field.value) + 1))
                                                            } else {
                                                                form.setValue("persons", "+")
                                                            }
                                                        }}>+</Button>
                                                    </div>
                                                </div>
                                            </FormItem>
                                            <FormMessage />
                                        </FormItem>
                                    )}
                                />
                            </div>
                        )
                    }
                    <CardFooter className="mt-5 px-0">
                        <Button
                            type="submit"
                            className={`
                            py-6 rounded-lg w-full font-semibold
                            ${false && 'bg-[#F2F4F7] text-[#A0A9B4]'}    
                        `}
                            disabled={form.getFieldState("date").invalid || form.getFieldState("persons").invalid}
                        >Continuar</Button>
                    </CardFooter>
                </form>
            </FormProvider>
        </div>
    )
}